import { useState } from 'react';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import { createWorkspace } from '../lib/resources';
import { Button, ErrorNote, Field, Modal, inputClass } from './ui';

const NEW = '__nova_empresa__';

/**
 * Troca de workspace (o seletor do topo). Emite token novo escopado no tenant.
 * Quem é dono de alguma empresa também cria outra por aqui ("+ Nova empresa…").
 */
export function WorkspaceSwitcher() {
  const { session, switchWorkspace } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [creating, setCreating] = useState(false);

  if (!session) return null;
  const isOwner = session.workspaces.some((workspace) => workspace.role === 'OWNER');

  const handleChange = async (workspaceId: string) => {
    if (workspaceId === NEW) {
      setCreating(true);
      return;
    }
    if (workspaceId === session.workspace.id) return;
    setSwitching(true);
    try {
      await switchWorkspace(workspaceId);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wide opacity-60">Workspace</span>
        <select
          value={session.workspace.id}
          disabled={switching}
          onChange={(event) => void handleChange(event.target.value)}
          className="mt-1 w-full rounded-lg border border-white/15 bg-white/10 px-2 py-1.5 text-sm outline-none disabled:opacity-50"
        >
          {session.workspaces.map((workspace) => (
            <option key={workspace.id} value={workspace.id} className="bg-white text-slate-900">
              {workspace.name}
            </option>
          ))}
          {isOwner && (
            <option value={NEW} className="bg-white text-slate-900">
              + Nova empresa…
            </option>
          )}
        </select>
      </label>
      {creating && (
        <NewWorkspaceModal
          onClose={() => setCreating(false)}
          onCreated={async (id) => {
            setCreating(false);
            setSwitching(true);
            try {
              await switchWorkspace(id);
            } finally {
              setSwitching(false);
            }
          }}
        />
      )}
    </>
  );
}

function NewWorkspaceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createWorkspace(name.trim());
      await onCreated(created.id);
    } catch (failure) {
      setError(errorMessage(failure, 'Não foi possível criar a empresa'));
      setBusy(false);
    }
  };

  return (
    <Modal title="Nova empresa" onClose={onClose}>
      <form
        className="space-y-4 text-slate-700"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim().length >= 2) void submit();
        }}
      >
        <Field
          label="Nome da empresa"
          hint="Ex.: Vânia Serafim. Cada empresa tem leads, pipelines, equipe, WhatsApp e aparência próprios."
        >
          <input
            autoFocus
            className={inputClass}
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <p className="text-xs text-slate-500">
          Você entra como dono. Ao criar, o CRM já abre a empresa nova.
        </p>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" disabled={name.trim().length < 2 || busy}>
            {busy ? 'Criando…' : 'Criar empresa'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
